import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Typewriter from 'typewriter-effect';
import NeonButton from '../shared/NeonButton';

const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  background-color: ${props => props.theme.colors.darkGray};
  color: ${props => props.theme.colors.white};
  overflow: hidden;
  padding: 0;
  padding-top: 80px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding-top: 120px;
    height: auto;
    min-height: 100vh;
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
  background-image: linear-gradient(
    to bottom right,
    ${props => props.theme.colors.darkGray},
    ${props => props.theme.colors.black}
  );
`;

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(
    ${props => props.theme.colors.darkGray} 1px,
    transparent 1px
  ),
  linear-gradient(
    to right,
    ${props => props.theme.colors.darkGray} 1px,
    transparent 1px
  );
  background-size: 50px 50px;
  opacity: 0.05;
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    padding-top: ${props => props.theme.spacing.lg};
    padding-bottom: ${props => props.theme.spacing.lg};
  }
`;

const HeroTextContainer = styled.div`
  max-width: 600px;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin-bottom: ${props => props.theme.spacing.xl};
  }
`;

const HeroTitle = styled(motion.div)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.sm};
  line-height: 1.1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 2.5rem;
    align-items: center;
  }
  
  .Typewriter {
    color: ${props => props.theme.colors.accent};
    margin-top: 0.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${props => props.theme.fontSizes.xlarge};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.large};
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const HeroImageContainer = styled(motion.div)`
  max-width: 500px;
  width: 100%;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    max-width: 400px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    max-width: 300px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    max-width: 250px;
  }
`;

const HeroVideo = styled.video`
  width: 100%;
  height: auto;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 15px rgba(61, 90, 254, 0.5));
  transition: all ${props => props.theme.transitions.default};
  border-radius: 8px;
  
  &:hover {
    filter: drop-shadow(0 15px 30px rgba(0, 0, 0, 0.4)) drop-shadow(0 0 25px rgba(61, 90, 254, 0.7));
    transform: translateY(-5px);
  }
`;

const Hero = () => {
  const videoRef = useRef(null);
  const isPlayingForwardRef = useRef(true);
  const animationFrameRef = useRef(null);
  const lastTimeRef = useRef(0);

  // Scroll to contact section function
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to products section function
  const scrollToProducts = () => {
    const productsSection = document.getElementById('projects');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Step size per frame for reverse playback (adjust for smoothness)
    const reverseStepSize = 1/60;
    
    // Handle forward playback reached end
    const handleForwardEnd = () => {
      if (isPlayingForwardRef.current && video.currentTime >= video.duration - 0.1) {
        // Switch to playing backward
        isPlayingForwardRef.current = false;
        video.pause();
        startReversePlayback();
      }
    };
    
    // Manual reverse playback using requestAnimationFrame
    const reverseStep = (timestamp) => {
      if (!lastTimeRef.current || timestamp - lastTimeRef.current >= 16) { // ~60fps
        lastTimeRef.current = timestamp;
        
        // Decrease time for reverse playback
        video.currentTime = Math.max(0, video.currentTime - reverseStepSize);
        
        // If we've reached near beginning, switch back to forward
        if (video.currentTime <= 0.1) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
          isPlayingForwardRef.current = true;
          video.play().catch(error => console.error("Error resuming forward playback:", error));
          return;
        }
      }
      
      // Continue reverse animation
      animationFrameRef.current = requestAnimationFrame(reverseStep);
    };
    
    // Start reverse playback animation
    const startReversePlayback = () => {
      lastTimeRef.current = 0;
      animationFrameRef.current = requestAnimationFrame(reverseStep);
    };

    // Start playback when video is ready
    const handleCanPlay = () => {
      isPlayingForwardRef.current = true;
      video.play().catch(error => console.error("Error starting video playback:", error));
    };

    // Handle touch events for mobile
    const handleTouch = () => {
      if (video.paused) {
        video.play().catch(error => console.error("Error playing video on touch:", error));
      }
    };

    // Restart video on hover
    const handleMouseEnter = () => {
      // Cancel any reverse animation if it's running
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      // Reset to forward playback from beginning
      isPlayingForwardRef.current = true;
      video.currentTime = 0;
      video.play().catch(error => console.error("Error restarting video on hover:", error));
    };

    // Add listeners
    video.addEventListener('timeupdate', handleForwardEnd);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('mouseenter', handleMouseEnter);
    video.addEventListener('touchstart', handleTouch);

    // Cleanup function
    return () => {
      if (video) {
        video.removeEventListener('timeupdate', handleForwardEnd);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('mouseenter', handleMouseEnter);
        video.removeEventListener('touchstart', handleTouch);
      }
      
      // Cancel animation frame on cleanup
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <HeroSection id="hero">
      <HeroBackground />
      <GridOverlay />
      <HeroContent>
        <HeroTextContainer>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span>We build</span>
            <Typewriter
              options={{
                strings: [
                  'web applications',
                  'software solutions',
                  'ecommerce shops',
                  'mobile experiences',
                  'digital products',
                  'automation solutions',
                  'AI-powered systems',
                ],
                autoStart: true,
                loop: true,
                delay: 75,
                deleteSpeed: 50,
              }}
            />
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            Innovative Technology Solutions for Tomorrow
          </HeroSubtitle>
          
          <ButtonContainer
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <NeonButton onClick={scrollToContact}>
              Contact Us
            </NeonButton>
            <NeonButton onClick={scrollToProducts}>
              Our Products
            </NeonButton>
          </ButtonContainer>
        </HeroTextContainer>
        
        <HeroImageContainer
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <HeroVideo 
            ref={videoRef}
            src="/videos/MAINvideo.mp4" 
            alt="Technology innovation illustration" 
            muted
            playsInline
          />
        </HeroImageContainer>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 